import type { TimelineRow } from '@tabel/shared/agent-events/timeline';
import type { AgentHealth } from '@tabel/shared/connector/protocol';
import { AGENTS, type AgentId } from '@tabel/shared/connector/versions';
import type { ConnectorView, ThreadDetail, ThreadStatus } from '@tabel/shared/schemas/agent-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CONNECTOR_ROLES,
  connectorsKey,
  listConnectors,
  threadDetail,
  threadTimeline,
} from '../../lib/agent-api';
import { type AgentSocket, useAgentSocket } from '../../lib/agent-ws';
import { ApiError, reportClientError } from '../../lib/errors';
import { useRole } from '../../lib/role';

/*
 * Данные «Разговора» 006 в браузере (T075; contracts/http-api.md, «Разговоры», «WebSocket браузера»; FR-020, US5-1).
 * - Лента (`useThreadTimeline`): подписка на разговор по WebSocket; `changed` с номером больше своего →
 *   `GET …/timeline?afterSequence=<свой номер>`; строки сливаются по постоянному ключу, ушедшие под свёрнутую работу
 *   агента (`removed`) убираются. Запрос один за раз: пока он идёт, новые `changed` дают один повтор после него.
 *   Новое соединение после обрыва (`open {reconnected}`) — догрузка с последнего номера. Догрузка не удалась, сервер
 *   сообщил новый номер, а догрузки нет 3 с, или нет связи дольше 5 с — лента «отстаёт» (полоса «Разговор
 *   загружается»).
 * - Разговор и очередь (`useThreadDetail`): `GET /api/agent/threads/:id`; состояние из `thread` по WebSocket — сразу в
 *   кэш.
 * - Компьютеры (`useAgentComputers`): тот же запрос и ключ кэша, что у «Мои ИИ-клиенты»; `connector`, `agents` и новое
 *   соединение — список заново. Роли без права, `403` (нет права «ИИ-агент») и `404` (функция выключена) — помощника нет.
 * Канал WebSocket несёт только номера и состояния: текст переписки приходит адресами.
 */

const SCREEN = 'Assistant';

export const threadKey = (id: string) => ['agent-thread', id] as const;
export const threadsKey = (archived: boolean) => ['agent-threads', archived ? 1 : 0] as const;
export const stepsKey = (threadId: string, runId: string) => ['agent-turn-steps', threadId, runId] as const;

/** Порядок строк: по номеру первого события; строка работы агента — после сообщения с тем же номером. */
export function rowOrder(a: TimelineRow, b: TimelineRow): number {
  return a.seq - b.seq || (a.kind === 'turn' ? 1 : 0) - (b.kind === 'turn' ? 1 : 0);
}

/** Слияние догрузки по ключу. Неизменённые строки остаются теми же объектами: строка не перерисовывается (FR-035). */
export function mergeRows(
  prev: readonly TimelineRow[],
  next: { rows: readonly TimelineRow[]; removed: readonly string[] },
): TimelineRow[] {
  const removed = new Set(next.removed);
  const byKey = new Map<string, TimelineRow>();
  for (const r of prev) if (!removed.has(r.key)) byKey.set(r.key, r);
  for (const r of next.rows) byKey.set(r.key, r);
  return [...byKey.values()].sort(rowOrder);
}

const report = (action: string, e: unknown) => {
  if (!(e instanceof ApiError) || e.code === 'NETWORK' || e.code === 'INTERNAL' || e.status >= 500)
    reportClientError(SCREEN, action, e);
};

/** Новый номер без догрузки дольше этого — лента отстаёт. */
const PENDING_LAG_MS = 3000;
/** Нет связи дольше этого — лента отстаёт. */
const OFFLINE_LAG_MS = 5000;

export interface Timeline {
  rows: readonly TimelineRow[];
  /** Последний номер, до которого лента догружена. */
  seq: number;
  /** Лента загружена хотя бы раз. */
  loaded: boolean;
  /** Последняя загрузка не удалась. */
  error: unknown;
  /** Лента отстаёт: догрузка не удалась или задержалась, или нет связи. */
  lagging: boolean;
  /** «Загрузить сейчас». */
  reload: () => void;
  /** Соединение вкладки: подписки на другие события. */
  socket: AgentSocket | null;
}

interface State {
  id: string | null;
  rows: readonly TimelineRow[];
  seq: number;
  loaded: boolean;
  error: unknown;
}

const empty = (id: string | null): State => ({ id, rows: [], seq: 0, loaded: false, error: null });

/**
 * Лента разговора: целиком при открытии, дальше — догрузка по `changed`. `onStatus` — состояние разговора из
 * WebSocket (`thread`).
 */
export function useThreadTimeline(
  threadId: string | null,
  onStatus?: (status: ThreadStatus) => void,
): Timeline {
  const [state, setState] = useState<State>(() => empty(threadId));
  const cur = useRef(state);
  cur.current = state;
  const busy = useRef(false);
  const again = useRef(false);
  /** Номер, о котором сообщил сервер. */
  const known = useRef(0);
  const [pendingSince, setPendingSince] = useState<number | null>(null);
  const [offlineSince, setOfflineSince] = useState<number | null>(null);
  const [late, setLate] = useState(false);
  const statusRef = useRef(onStatus);
  statusRef.current = onStatus;

  // Другой разговор — лента с нуля.
  if (state.id !== threadId) {
    const fresh = empty(threadId);
    cur.current = fresh;
    setState(fresh);
  }

  const load = useCallback(async (id: string) => {
    if (busy.current) {
      again.current = true;
      return;
    }
    busy.current = true;
    try {
      do {
        again.current = false;
        const after = cur.current.id === id ? cur.current.seq : 0;
        let res: Awaited<ReturnType<typeof threadTimeline>>;
        try {
          res = await threadTimeline(id, after > 0 ? after : undefined);
        } catch (e) {
          report('load timeline', e);
          if (cur.current.id === id) {
            const next = { ...cur.current, error: e };
            cur.current = next;
            setState(next);
          }
          return;
        }
        if (cur.current.id !== id) return;
        const base = cur.current;
        const next: State = {
          id,
          rows: after > 0 ? mergeRows(base.rows, res) : [...res.rows].sort(rowOrder),
          seq: Math.max(after, res.seq),
          loaded: true,
          error: null,
        };
        cur.current = next;
        setState(next);
        if (next.seq >= known.current) setPendingSince(null);
      } while (again.current);
    } finally {
      busy.current = false;
    }
  }, []);

  useEffect(() => {
    known.current = 0;
    setPendingSince(null);
    if (threadId) void load(threadId);
  }, [threadId, load]);

  const socket = useAgentSocket((e) => {
    if (!threadId) return;
    if (e.op === 'changed' && e.threadId === threadId) {
      known.current = Math.max(known.current, e.seq);
      if (e.seq > cur.current.seq) {
        setPendingSince((p) => p ?? Date.now());
        void load(threadId);
      }
    } else if (e.op === 'thread' && e.threadId === threadId) {
      statusRef.current?.(e.status);
    } else if (e.op === 'open') {
      setOfflineSince(null);
      if (e.reconnected) void load(threadId);
    } else if (e.op === 'closed') {
      setOfflineSince((p) => p ?? Date.now());
    }
  }, threadId !== null);

  useEffect(() => {
    if (!socket || !threadId) return;
    socket.subscribe(threadId);
    return () => socket.unsubscribe(threadId);
  }, [socket, threadId]);

  // Отставание: таймер на время ожидания догрузки или связи.
  useEffect(() => {
    const waits = [
      pendingSince === null ? null : pendingSince + PENDING_LAG_MS,
      offlineSince === null ? null : offlineSince + OFFLINE_LAG_MS,
    ].filter((x): x is number => x !== null);
    if (waits.length === 0) {
      setLate(false);
      return;
    }
    const at = Math.min(...waits);
    const left = at - Date.now();
    if (left <= 0) {
      setLate(true);
      return;
    }
    setLate(false);
    const t = setTimeout(() => setLate(true), left);
    return () => clearTimeout(t);
  }, [pendingSince, offlineSince]);

  const reload = useCallback(() => {
    if (threadId) void load(threadId);
  }, [threadId, load]);

  const sameThread = state.id === threadId;
  return {
    rows: sameThread ? state.rows : [],
    seq: sameThread ? state.seq : 0,
    loaded: sameThread && state.loaded,
    error: sameThread ? state.error : null,
    lagging: threadId !== null && sameThread && (state.error !== null || late),
    reload,
    socket,
  };
}

/** Разговор и очередь. Состояние из WebSocket (`thread`) кладётся в кэш сразу. */
export function useThreadDetail(threadId: string | null) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: threadKey(threadId ?? ''),
    queryFn: async () => {
      try {
        return await threadDetail(threadId ?? '');
      } catch (e) {
        report('load thread', e);
        throw e;
      }
    },
    enabled: threadId !== null,
  });
  const setStatus = useCallback(
    (status: ThreadStatus) => {
      if (!threadId) return;
      qc.setQueryData<ThreadDetail>(threadKey(threadId), (d) =>
        d ? { ...d, thread: { ...d.thread, status } } : d,
      );
      void qc.invalidateQueries({ queryKey: threadKey(threadId) });
    },
    [qc, threadId],
  );
  return { ...query, setStatus };
}

/** Компьютер и агент нового разговора. */
export interface AgentChoice {
  connectorId: number;
  agent: AgentId;
}

const CHOICE_KEY = 'tabel.assistant.agent';

export function readChoice(): AgentChoice | null {
  try {
    const [c, a] = (localStorage.getItem(CHOICE_KEY) ?? '').split(':');
    const id = Number(c);
    return Number.isInteger(id) && id > 0 && (AGENTS as readonly string[]).includes(a ?? '')
      ? { connectorId: id, agent: a as AgentId }
      : null;
  } catch {
    return null;
  }
}

export function saveChoice(c: AgentChoice): void {
  try {
    localStorage.setItem(CHOICE_KEY, `${c.connectorId}:${c.agent}`);
  } catch {
    // хранилище недоступно — выбор живёт до перезагрузки
  }
}

/**
 * Выбор по умолчанию: сохранённый, если компьютер не отключён; иначе первый готовый агент на компьютере в сети
 * (порядок Claude, Codex, pi), иначе первый агент первого компьютера.
 */
export function defaultChoice(list: readonly ConnectorView[], saved: AgentChoice | null): AgentChoice | null {
  const active = list.filter((c) => c.phase === 'active');
  if (saved && active.some((c) => c.id === saved.connectorId)) return saved;
  const order = (h: AgentHealth) => AGENTS.indexOf(h.agent);
  for (const c of [...active.filter((x) => x.online), ...active.filter((x) => !x.online)]) {
    const ready = [...c.agents].sort((a, b) => order(a) - order(b)).find((h) => h.state === 'ready');
    if (ready) return { connectorId: c.id, agent: ready.agent };
  }
  const first = active[0];
  return first ? { connectorId: first.id, agent: first.agents[0]?.agent ?? 'claude' } : null;
}

/** Свои компьютеры и агенты для помощника. */
export function useAgentComputers() {
  const { role } = useRole();
  const eligible = (CONNECTOR_ROLES as readonly string[]).includes(role);
  const query = useQuery({ queryKey: connectorsKey(role), queryFn: listConnectors, enabled: eligible });
  const refetch = query.refetch;
  const available = eligible && query.isSuccess;
  // Тот же список слушает и «Мои ИИ-клиенты»: идущий запрос не отменяется — список грузится один раз.
  useAgentSocket((e) => {
    if (e.op === 'connector' || e.op === 'agents' || (e.op === 'open' && e.reconnected))
      void refetch({ cancelRefetch: false });
  }, available);
  const list = available ? query.data : [];
  return {
    /** Помощник есть: роль с правом, право «ИИ-агент», функция включена. */
    available,
    list,
    refetch,
    computer: (id: number | null | undefined) => list.find((c) => c.id === id) ?? null,
    health: (id: number | null | undefined, agent: AgentId | null | undefined) =>
      list.find((c) => c.id === id)?.agents.find((h) => h.agent === agent) ?? null,
  };
}
